import React, { useState } from 'react';
import Checkbox, { CheckboxChangeEvent } from 'antd/es/checkbox';
import {
	VesselTypeLabels,
	VesselTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { CargoTypeProps } from '@api/models/cargo-type';
import showErrorNotification from '@client/utils/showErrorNotification';
import useFetchedState from '@client/utils/hooks/useFetchedState';
import {
	createCargoType,
	deleteCargoType,
	getUserData,
	updateCargoType,
} from '@client/lib/api';
import EditableTable from '@client/components/EditableTable';

const CargoTypesSettings = () => {
	const [userData, refreshUserData] = useFetchedState(getUserData);
	const [editingValues, setEditingValues] = useState<Partial<CargoTypeProps> | null>(null);

	const columns = [
		{
			dataIndex: 'name',
			title: 'Name',
			editable: true,
			editingProps: {
				type: 'text',
				inputProps: {
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							name: v.target.value as string,
						}));
					},
				},
			},
			width: 200,
		},
		{
			dataIndex: 'group',
			title: 'Group',
			editable: true,
			editingProps: {
				type: 'text',
				inputProps: {
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							group: v.target.value as string,
						}));
					},
				},
			},
			width: 200,
		},
		{
			dataIndex: 'vesselTypes',
			title: 'Vessel Type',
			editable: true,
			editingProps: {
				mode: 'multiple',
				type: 'select',
				options: (Object.keys(VesselTypes) as Array<keyof typeof VesselTypes>).map((t) => ({
					label: VesselTypeLabels[t],
					value: t,
				})),
				inputProps: {
					mode: 'multiple',
					showSearch: true,
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							vesselTypes: v as Values<typeof VesselTypes>[],
						}));
					},
				},
			},
			width: 230,
			render: (t: Values<typeof VesselTypes>[]) => t?.length > 0 ? t.map((t) => VesselTypeLabels[t]).join(', ') : '',
		},
		{
			dataIndex: 'stowageFactorCBM',
			title: 'Stowage Factor CBM/MT',
			editable: true,
			editingProps: {
				type: 'number',
				inputProps: {
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							stowageFactorCBM: v as number,
						}));
					},
				},
			},
			width: 200,
			render: (v: number) => (v != null ? `${v} CBM/MT` : ''),
		},
		{
			dataIndex: 'stowageFactorCBFT',
			title: 'Stowage Factor CBFT/MT',
			editable: true,
			editingProps: {
				type: 'number',
				inputProps: {
					onChange: (v: any) => {
						setEditingValues((prev) => ({
							...prev,
							stowageFactorCBFT: v as number,
						}));
					},
				},
			},
			width: 200,
			render: (v: number) => (v != null ? `${v} CBFT/MT` : ''),
		},
		{
			dataIndex: 'active',
			title: 'Active',
			editable: true,
			align: 'center',
			checked: true,
			editingProps: {
				type: 'check',
				defaultValue: editingValues?.active ?? true,
				inputProps: {
					onChange: (v: CheckboxChangeEvent) => {
						setEditingValues((prev) => ({
							...prev,
							active: v.target.checked,
						}));
					},
				},
			},
			width: 200,
			render: (c: boolean) => (<Checkbox disabled checked={c} />),
		},
	];

	const handleAddNewCargoType = async () => {
		if (!editingValues?.name) {
			showErrorNotification('Name is required');

			return;
		}

		const {
			name,
			group,
			stowageFactorCBM,
			stowageFactorCBFT,
			vesselTypes,
			active,
		} = editingValues;

		if (!name) {
			showErrorNotification('Missing required data');

			return;
		}

		await createCargoType({
			name,
			vesselTypes,
			group,
			stowageFactorCBM,
			stowageFactorCBFT,
			active: active ?? true,
		});

		await refreshUserData();
		setEditingValues(null);
	};

	const handleUpdateCargoType = async (id: number, row: CargoTypeProps) => {
		const finalValues = {
			...row,
			...editingValues,
		};

		if (finalValues.id == null) {
			throw showErrorNotification('Cargo type is required');
		}

		const {
			name,
			group,
			stowageFactorCBM,
			stowageFactorCBFT,
			active,
			vesselTypes,
		} = finalValues;

		await updateCargoType({
			id,
			name,
			group,
			stowageFactorCBM: Number(stowageFactorCBM ?? 0),
			stowageFactorCBFT: Number(stowageFactorCBFT ?? 0),
			active,
			vesselTypes: vesselTypes ?? [],
		});

		await refreshUserData();
		setEditingValues(null);
	};

	const handleDeleteCargoType = async (id: number) => {
		await deleteCargoType({ id });
		await refreshUserData();
	};

	return (
		<>
			<EditableTable<CargoTypeProps, 'id'>
				size="small"
				pagination={false}
				keyDataIndex="id"
				rowKey="id"
				key={userData?.cargoTypes.length}
				columns={columns as any}
				dataSource={userData?.cargoTypes ?? []}
				editingRow={editingValues?.id ?? null}
				enableEdit
				onEditingRowChange={(_, row) => {
					setEditingValues(row as Partial<CargoTypeProps> | null);
				}}
				onSave={handleUpdateCargoType}
				onAddNew={handleAddNewCargoType}
				deleteMessage="Are you sure you want to remove this entry"
				onDelete={handleDeleteCargoType}
				allowAddNew
				addNewText="Cargo Type"
				enableDelete
				newRowOnTop
			/>
		</>
	);
};

export default CargoTypesSettings;
